import {
  AgentSessionStepType,
  AgentSessionType,
} from "@/modules/sessions/types";
import { ZButton } from "@/shared/components/button";
import AcceptAndRejectPopOver from "@/shared/components/generate-with-ai/AcceptAndRejectPopOver";
import GenerateWithAI from "@/shared/components/generate-with-ai/GenerateWithAI";
import { ZTooltip } from "@/shared/components/ZTooltip/ZTooltip";
import { AiResponseType } from "@/shared/types/user";
import { cn } from "@/shared/utils/classname-merger";
import {
  Checkbox,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  Tooltip,
} from "@nextui-org/react";
import clsx from "clsx";
import { ChevronDown, Info, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getAgentData, useReassessmentModeStore } from "../../../states";
import {
  CustomGapAssessmentControl,
  getCustomizeControls,
  getCustomizeControlsActions,
  getCustomizeControlsAiOpenStates,
  SecurityControlCategoryOption,
  Soc2Control,
  TSCABBRIVATIONS,
  TSCENUM,
  UploadModalServiceCriteriaLabel,
  useCustomizeControlsStore,
} from "../../../states/cusomize-controls";
import {
  RefineControlDescriptionResponseForSoc2,
  refineControlsDescription,
  refineControlsTestingProcedure,
} from "../../../use-cases/customize-control-ai-generate.use-case";
import {
  isInValidIdForSoc2,
  ZInputWithOnChangeDelay,
  ZTextAreaWithOnChangeDelay,
  ZTextAreaWithOnChangeDelaySoc2,
} from "../helpers";
import ReassessDocumentsModal from "../ReassessDocumentsModal";
import UploadDocumentsModal from "../UploadDocumentsModal";

const useReassessmentDisabled = () => {
  const { isReassessmentMode, agentId: reassessmentAgentId } = useReassessmentModeStore();
  const { id: currentAgentId } = useParams<{ id: string }>();
  return isReassessmentMode && currentAgentId === reassessmentAgentId;
};

interface baseProps {
  control: Soc2Control;
  searchQuery: string;
  isDisabled?: boolean;
  handleChangeDataState: (
    key: keyof Soc2Control,
    uId: string,
    newValue: string,
    changesInTestingProcedure?: number
  ) => void;
}

interface CustomizeControlCheckboxRendererProps extends baseProps {
  selectedControls: Set<string>;
  setSelectedControls: (
    newVal: Set<string> | ((prev: Set<string>) => Set<string>)
  ) => void;
}

interface CustomizeControlIdRendererProps extends baseProps {
  setAccordianOpenState: (key: TSCENUM, newValue: boolean) => void;
  accordianOpenState: {
    [key in TSCENUM]: boolean;
  };
}

export const CustomizeControlCheckboxRenderer = ({
  control,
  selectedControls,
  setSelectedControls,
}: CustomizeControlCheckboxRendererProps) => {
  const { id = "" } = useParams<{ id: string }>();
  const isCurrentSessionInReassessment = useReassessmentDisabled();
  const isCheckboxDisabled = getCustomizeControlsAiOpenStates() > 0;

  // Check if we're in reassessment mode for SOC1/SOC2 agent types
  const agentData = getAgentData(id);
  const isSOC1OrSOC2Agent = agentData?.sessionData.type === AgentSessionType.SOC2_GAP ||
                            agentData?.sessionData.type === AgentSessionType.SOC2_GAP_TYPE1 ||
                            agentData?.sessionData.type === AgentSessionType.SOC2_GAP_TYPE2;
  
  // Enable checkboxes in reassessment mode for SOC1/SOC2 agents
  const shouldEnableCheckboxInReassessment = isCurrentSessionInReassessment && isSOC1OrSOC2Agent;

  return (
    <div className="flex justify-center items-center w-full min-h-[40px]">
      <Checkbox
        isSelected={selectedControls.has(control.uid)}
        classNames={{
          base: cn("group", control.edited_state === "NEW" && "invisible"),
          wrapper: cn(
            "group-data-[disabled=true]:border-default",
            "after:group-data-[disabled=true]:bg-default-100"
          ),
          icon: "group-data-[disabled=true]:text-default-300",
        }}
        onChange={() => {
          setSelectedControls((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(control.uid)) {
              newSet.delete(control.uid);
            } else {
              newSet.add(control.uid);
            }
            return newSet;
          });
        }}
        isDisabled={isCheckboxDisabled || (isCurrentSessionInReassessment && !shouldEnableCheckboxInReassessment)}
        disabled={isCheckboxDisabled || (isCurrentSessionInReassessment && !shouldEnableCheckboxInReassessment)}
        data-component="z-table-row-select"
        color={"primary"}
      />
    </div>
  );
};

export const CustomizeControlIdRenderer = ({
  control,
  handleChangeDataState,
  searchQuery,
  setAccordianOpenState,
  accordianOpenState,
  isDisabled = false,
}: CustomizeControlIdRendererProps) => {
  const [isActive, setIsActive] = useState(false);
  const isCurrentSessionInReassessment = useReassessmentDisabled();

  const listOfid = (getCustomizeControls() as Soc2Control[])
    .filter((d) => d.uid !== control.uid)
    .filter(
      (ctl) =>
        control.tsc_id.replace(TSCABBRIVATIONS[ctl.tsc], "").trim().length > 0
    )
    .map((d) => d.tsc_id);

  const isDuplicateId = listOfid.includes(control.tsc_id);

  const isInIdValid =
    control.tsc_id?.replace(TSCABBRIVATIONS[control.tsc], "").length > 0 &&
    isInValidIdForSoc2(
      control.tsc_id?.replace(TSCABBRIVATIONS[control.tsc], "")
    );

  return (
    <div className={cn("flex w-[100px] pl-2")}>
      <div className="flex flex-col">
        <div className="flex">
          <ZInputWithOnChangeDelay
            id={`${control.uid}tsc_id`}
            aria-label={`${control.uid}tsc_id`}
            classNames={{
              inputWrapper:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              base:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              clearButton:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              description:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              errorMessage:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              helperWrapper:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              innerWrapper:
                "bg-transparent p-0 pl-[6px] data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              input:
                "bg-transparent p-[6px] pl-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              label:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              mainWrapper:
                "bg-transparent p-0  data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
            }}
            startContent={TSCABBRIVATIONS[control.tsc]}
            placeholder="Add ID"
            value={control.tsc_id
              ?.replace(TSCABBRIVATIONS[control.tsc], "")
              .trim()}
            onChange={(e) => {
              handleChangeDataState(
                "tsc_id",
                control.uid,
                TSCABBRIVATIONS[control.tsc] + e.target.value.trim()
              );
            }}
            disabled={isDisabled || searchQuery.length > 0 || isCurrentSessionInReassessment}
            isInvalid={isInIdValid || isDuplicateId}
            onFocus={() => setIsActive(true)}
            onBlur={() => setIsActive(false)}
            autoFocus={false}
          />
        </div>
        <div className="flex flex-row">
          <div className="pl-0">
            {(isInIdValid || isDuplicateId) && (
              <div className="flex flex-row gap-1 py-1.5 ">
                {isInIdValid && (
                  <ZTooltip
                    isDisabled={false}
                    showArrow={true}
                    content={
                      <div>
                        Expected Format: {TSCABBRIVATIONS[control.tsc]} 1.2.3
                      </div>
                    }
                    delay={500}
                    closeDelay={0}
                    placement="bottom-start"
                  >
                    <span>
                      <Info size={16} strokeWidth={1.5} color="#F31260" />
                    </span>
                  </ZTooltip>
                )}
                <div className="text-[#F31260] text-xs font-medium break-keep max-w-[72px]">
                  {isInIdValid
                    ? <>Invalid&nbsp;Format</>
                    : isDuplicateId
                      ? "Duplicate ID"
                      : ""}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface CustomizeCustomAssessmentControlIdRendererProps extends baseProps {}

export const CustomizeCustomAssessmentControlIdRenderer = ({
  control,
  handleChangeDataState,
  searchQuery,
  isDisabled = false,
}: CustomizeCustomAssessmentControlIdRendererProps) => {
  const [isActive, setIsActive] = useState(false);

  const listOfid = (getCustomizeControls() as Soc2Control[])
    .filter((d) => d.control_id !== control.control_id)
    .map((d) => d.uid);

  const isDuplicateId = listOfid.includes(control.control_id);

  const isInIdValid = false;

  return (
    <div className={cn("flex min-w-[120px] pl-2")}>
      <div className="flex flex-col">
        <div className="flex">
          <ZInputWithOnChangeDelay
            id={`${control.uid}_id`}
            aria-label={`${control.uid}_id`}
            classNames={{
              inputWrapper:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              base:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              clearButton:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              description:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              errorMessage:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              helperWrapper:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              innerWrapper:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              input:
                "bg-transparent p-[6px] pl-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              label:
                "bg-transparent p-0 data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
              mainWrapper:
                "bg-transparent p-0  data-[hover=true]:bg-transparent shadow-none border-none active:bg-white",
            }}
            startContent={control.controlIdPrefix}
            placeholder="Add ID"
            value={control.control_id}
            onChange={(e) => {
              handleChangeDataState(
                "control_id",
                control.uid,
                e.target.value.trim()
              );
            }}
            disabled={isDisabled || searchQuery.length > 0}
            isInvalid={isInIdValid || isDuplicateId}
            onFocus={() => setIsActive(true)}
            onBlur={() => setIsActive(false)}
            autoFocus={false}
          />
        </div>
        <div className="flex flex-row">
          <div className="pl-6">
            {(isInIdValid || isDuplicateId) && (
              <div className="flex flex-row gap-1 p-1.5 ">
                {isInIdValid && (
                  <Tooltip
                    showArrow={true}
                    content={
                      <div>
                        Expected Format: {control.controlIdPrefix} 1.2.3
                      </div>
                    }
                    delay={500}
                    closeDelay={0}
                    classNames={{
                      content:
                        "bg-black bg-opacity-80 backdrop-blur-md text-white text-tiny max-w-[600px]",
                      arrow: "bg-black bg-opacity-80 backdrop-blur-md",
                    }}
                    placement="bottom-start"
                  >
                    <span>
                      <Info size={16} strokeWidth={1.5} color="#F31260" />
                    </span>
                  </Tooltip>
                )}
                <div className="text-[#F31260] text-xs font-medium">
                  {isInIdValid
                    ? "Invalid Format"
                    : isDuplicateId
                      ? "Duplicate ID"
                      : ""}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

interface CustomizeControlCategoryRendererProps extends baseProps {}
export const CustomizeControlCategoryRenderer = ({
  control,
  handleChangeDataState,
  searchQuery,
  isDisabled,
}: CustomizeControlCategoryRendererProps) => {
  const isCurrentSessionInReassessment = useReassessmentDisabled();
  
  return (
    <div className="w-[240px] pt-3">
      {control.edited_state !== "ACCORDIAN" && (
        <div>
          <ZSelectCategory
            control={control}
            handleChangeDataState={handleChangeDataState}
            searchQuery={searchQuery}
            isDisabled={isDisabled || isCurrentSessionInReassessment}
          />
        </div>
      )}
    </div>
  );
};

interface ZSelectCategoryProps {
  control: Soc2Control;
  handleChangeDataState: (
    key: keyof Soc2Control,
    uId: string,
    newValue: string
  ) => void;
  searchQuery: string;
  isDisabled?: boolean;
}

const ZSelectCategory = ({
  control,
  handleChangeDataState,
  searchQuery,
  isDisabled = false,
}: ZSelectCategoryProps) => {
  const [isOpen, setisOpen] = useState(false);

  if(isDisabled){
    return <div className="text-zinc-400">
      {control.category}
    </div>
  }

  return (
    <div className="flex flex-col">
      <Dropdown
        // aria-expanded={false}
        isDisabled={isDisabled || control.tsc !== TSCENUM.SECURITY}
        classNames={{
          trigger: cn(
            "!scale-[1]  !opacity-100 p-3 px-[6px]",
            isOpen &&
            "bg-white p-[11px] px-[5px] border-1 border-solid border-[#A5A5A5] rounded-[4px]"
          ),
        }}
        onOpenChange={(isOpenChange) => {
          setisOpen(isOpenChange);
        }}
      >
        <DropdownTrigger className="">
          <div className={cn("flex flex-row items-center justify-between")}>
            <div
              className={`text-ellipsis line-clamp-1 ${
                control.category ? "text-zinc-700" : "text-zinc-400"
                } `}
            >
              {control.category
                ? SecurityControlCategoryOption.find(
                  (c) => c.value === control.category
                )?.label ?? ""
                : control.tsc !== TSCENUM.SECURITY
                  ? "N/A"
                  : "Select Category"}
            </div>

            <div>
              {control.tsc === TSCENUM.SECURITY && (
                <ChevronDown className="h-5 w-4" />
              )}
            </div>
          </div>
        </DropdownTrigger>
        <DropdownMenu
          selectedKeys={new Set([control.category])}
          selectionMode="single"
        >
          {SecurityControlCategoryOption.map((cat) => (
            <DropdownItem
              key={cat.value}
              onClick={() => {
                handleChangeDataState("category", control.uid, cat.value);
              }}
            >
              {cat.label}
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};


interface EditableColumnRendererProps extends baseProps {
  columnKey: string;                // The main key for the column, e.g., "custom_field_xyz"
  label?: string;                   // Optional: for accessibility/placeholder
  placeholder?: string;             // Optional placeholder
  disableAIRefine?: boolean;        // If you want to turn off AI refine for custom fields (optional)
  control: any;
}

export const EditableColumnRenderer = ({
  control,
  handleChangeDataState,
  searchQuery,
  isDisabled = false,
  columnKey,
  label,
  placeholder,
  disableAIRefine = false,
}: EditableColumnRendererProps) => {
  const { id = "" } = useParams<{ id: string }>();
  const isCurrentSessionInReassessment = useReassessmentDisabled();

  // ---- Dynamic key access ----
  // Handles values and new_ values for that key
  const mainKey = columnKey;
  const newKey = `new_${columnKey}`;
  const value = control?.[mainKey] ?? "";
  const newValue = control?.[newKey] ?? undefined;

  // Hide for certain 'edited_state' conditions if you want
  if (control.edited_state === "ACCORDIAN") {
    return null;
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAcceptAndRejectPopover, setShowAcceptAndRejectPopover] = useState<false | "SUCCESS" | "ERROR">(false);

  // ---- AI refine handler (generic) ----
  const handleClickForRefine = useCallback(async () => {
    try {
      setIsLoading(true);
      // You need to update this logic to send the correct 'field'/'key' to your backend
      const agentData = getAgentData(id);
      let response_quality =
        agentData?.sessionData.type === AgentSessionType.SOC2_GAP
          ? agentData?.sessionData.soc2_type2_audit.response_quality
          : agentData?.sessionData.type === AgentSessionType.SOC2_GAP_TYPE1
            ? agentData?.sessionData.soc2_type1.response_quality
            : agentData?.sessionData.type === AgentSessionType.SOC2_GAP_TYPE2
              ? agentData?.sessionData.soc2_type2.response_quality
              : AiResponseType.LITE;

      if(
        response_quality === undefined ||
        response_quality === null ||
        response_quality === AiResponseType.NONE
      ) {
        response_quality = AiResponseType.LITE;
      }

      // Dynamically pass field and its value to the backend
      const controlPayload = { ...control, [`${mainKey}`]: control[mainKey] };

      const resp = (await refineControlsDescription({
        task: AgentSessionStepType.SOC2_TYPE2,
        controls: [controlPayload],
        response_quality,
      })) as any; // update type if you have a type

      // --- Update custom field with response ---
      if (resp.data.controls[0] && resp.data.controls[0][mainKey]) {
        handleChangeDataState(
          newKey as keyof Soc2Control,
          control.uid,
          resp.data.controls[0][mainKey]
        );
      }

      setIsLoading(false);
      if (showAcceptAndRejectPopover === false) {
        getCustomizeControlsActions().setNoOfGenerateWithAiPopoverOpens(prev => prev + 1);
      }
      setShowAcceptAndRejectPopover("SUCCESS");
    } catch (err) {
      setIsLoading(false);
      if (showAcceptAndRejectPopover === false) {
        getCustomizeControlsActions().setNoOfGenerateWithAiPopoverOpens(prev => prev + 1);
      }
      setShowAcceptAndRejectPopover("ERROR");
      throw new Error(`Failed to refine field: ${mainKey}`);
    }
  }, [control, handleChangeDataState, id, showAcceptAndRejectPopover, mainKey, newKey]);

  // ---- Accept/Retry/Discard Handler (same as before, but dynamic key) ----
  const handleClickForAcceptRetryRejectButtons = useCallback(
    (action: "CONTINUE" | "DISCARD") => {
      const { setDataState } = getCustomizeControlsActions();

      if (action === "DISCARD") {
        setShowAcceptAndRejectPopover(false);
        getCustomizeControlsActions().setNoOfGenerateWithAiPopoverOpens(prev => prev - 1);
        setDataState((prev) => {
          const newValue = [...(prev as CustomGapAssessmentControl[])];
          const findIndexOfEditingControl = newValue.findIndex((d) => d.uid === control.uid);
          if (findIndexOfEditingControl > -1) {
            newValue[findIndexOfEditingControl][newKey] = undefined;
          }
          return newValue;
        });
      }
      if (action === "CONTINUE") {
        if (showAcceptAndRejectPopover === "ERROR") {
          handleClickForRefine();
        }
        if (showAcceptAndRejectPopover === "SUCCESS") {
          setDataState((prev) => {
            const newValue = [...(prev as CustomGapAssessmentControl[])];
            const findIndexOfEditingControl = newValue.findIndex((d) => d.uid === control.uid);
            if (findIndexOfEditingControl > -1) {
              const updated = newValue[findIndexOfEditingControl];
              updated[mainKey] = updated[newKey] ?? updated[mainKey];
              updated[newKey] = undefined;
              // Update generic trackers if needed, otherwise you can skip these!
              updated.changesInControl = 0;
              updated.changesInTestingProcedure = 1;
            }
            return newValue;
          });
          setShowAcceptAndRejectPopover(false);
          getCustomizeControlsActions().setNoOfGenerateWithAiPopoverOpens(prev => prev - 1);
          getCustomizeControlsActions().setChangesMade(prev => prev + 1); // autosave
        }
      }
    },
    [handleClickForRefine, showAcceptAndRejectPopover, control.uid, mainKey, newKey]
  );

  // --- Effects, focus, etc (same as original) ----

  useEffect(() => {
    // This is the same as your original scroll/overflow handling
    // Copy-paste or abstract as needed!
    // ...
  }, [showAcceptAndRejectPopover]);

  useEffect(() => {
    // This is the same as your original focus row handling
    // ...
  }, [control, mainKey]);

  return (
    <div className="h-full min-w-[280px]" id={control.control_id} ref={containerRef}>
      {/* Main field editor */}
      <ZTextAreaWithOnChangeDelay
        id={`${control.uid}${mainKey}`}
        aria-label={label || mainKey}
        className={cn(
          "text-zinc-700 font-light bg-transparent focus-within:text-[#000000] mb-[5px]",
          newValue !== undefined && "mb-0"
        )}
        classNames={{
          base: "shadow-none  border-none hover:border-none rounded-none truncate",
          inputWrapper: ` text-sm font-normal text-zinc-700 p-px border-none`,
          input: "p-[4px]",
        }}
        forceActive={isLoading || showAcceptAndRejectPopover !== false}
        strikeContent={showAcceptAndRejectPopover !== false ? newValue !== undefined : undefined}
        minRows={3}
        maxRows={8}
        isDisabled={isDisabled || searchQuery.length > 0 || isCurrentSessionInReassessment}
        variant="bordered"
        labelPlacement="outside"
        value={value}
        placeholder={placeholder || "Add value"}
        onChange={(e) =>
          handleChangeDataState(mainKey as any, control.uid, e.target.value)
        }
        autoFocus={false}
      />

      {/* new_ field editor, if exists */}
      {newValue !== undefined && (
        <ZTextAreaWithOnChangeDelay
          id={`${control.uid}${newKey}`}
          aria-label={`${mainKey} (new value)`}
          className="text-zinc-700 font-light bg-transparent  focus-within:text-[#000000] mb-[5px]"
          classNames={{
            base: "shadow-none  border-none hover:border-none rounded-none truncate",
            inputWrapper: ` text-sm font-normal text-zinc-700 p-px border-none`,
            input: "p-[4px]",
          }}
          forceActive={true}
          strikeContent={false}
          minRows={3}
          maxRows={8}
          disabled={searchQuery.length > 0 || isCurrentSessionInReassessment}
          variant="bordered"
          labelPlacement="outside"
          value={newValue}
          placeholder={placeholder || "Add value"}
          onChange={(e) =>
            handleChangeDataState(newKey as any, control.uid, e.target.value)
          }
          autoFocus={false}
        />
      )}
    </div>
  );
};

interface CustomizeControlDescriptionRendererProps extends baseProps {}
export const CustomizeControlDescriptionRenderer = ({
  control,
  handleChangeDataState,
  searchQuery,
  isDisabled = false,
}: CustomizeControlDescriptionRendererProps) => {
  const { id = "" } = useParams<{ id: string }>();
  const isCurrentSessionInReassessment = useReassessmentDisabled();

  const [isRowActive, setIsRowActive] = useState(false);

  if (control.edited_state === "ACCORDIAN") {
    return null;
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAcceptAndRejectPopover, setShowAcceptAndRejectPopover] = useState<
    false | "SUCCESS" | "ERROR"
  >(false);

  const handleClickForRefineControls = useCallback(async () => {
    try {
      setIsLoading(true);
      const agentData = getAgentData(id);
      let response_quality =
        agentData?.sessionData.type === AgentSessionType.SOC2_GAP
          ? agentData?.sessionData.soc2_type2_audit.response_quality
          : agentData?.sessionData.type === AgentSessionType.SOC2_GAP_TYPE1
            ? agentData?.sessionData.soc2_type1.response_quality
            : agentData?.sessionData.type === AgentSessionType.SOC2_GAP_TYPE2
              ? agentData?.sessionData.soc2_type2.response_quality
              : AiResponseType.LITE;

      if (
        response_quality === undefined ||
        response_quality === null ||
        response_quality === AiResponseType.NONE
      ) {
        response_quality = AiResponseType.LITE;
      }

      const resp = (await refineControlsDescription({
        task: AgentSessionStepType.SOC2_TYPE2,
        controls: [control],
        response_quality,
      })) as RefineControlDescriptionResponseForSoc2;

      if (resp.data.controls[0].control) {
        handleChangeDataState(
          "new_control",
          control.uid,
          resp.data.controls[0].control
        );
      }

      setIsLoading(false);
      if (showAcceptAndRejectPopover === false) {
        getCustomizeControlsActions().setNoOfGenerateWithAiPopoverOpens(
          (prev) => prev + 1
        );
      }
      setShowAcceptAndRejectPopover("SUCCESS");
    } catch (err) {
      setIsLoading(false);
      if (showAcceptAndRejectPopover === false) {
        getCustomizeControlsActions().setNoOfGenerateWithAiPopoverOpens(
          (prev) => prev + 1
        );
      }
      setShowAcceptAndRejectPopover("ERROR");
      throw new Error("Failed to refine control descroiption ");
    }
  }, [control, handleChangeDataState, id, showAcceptAndRejectPopover]);

  const handleClickForAcceptRetryRejectButtons = useCallback(
    (action: "CONTINUE" | "DISCARD") => {
      const { setDataState } = getCustomizeControlsActions();

      if (action === "DISCARD") {
        setShowAcceptAndRejectPopover(false);
        getCustomizeControlsActions().setNoOfGenerateWithAiPopoverOpens(
          (prev) => prev - 1
        );
        setDataState((prev) => {
          const newValue = [...(prev as Soc2Control[])];

          const findIndexOfEditingControl = newValue.findIndex(
            (d) => d.uid === control.uid
          );
          if (findIndexOfEditingControl > -1) {
            newValue[findIndexOfEditingControl].new_control = undefined;
          }

          return newValue;
        });
      }
      if (action === "CONTINUE") {
        if (showAcceptAndRejectPopover === "ERROR") {
          handleClickForRefineControls();
        }
        if (showAcceptAndRejectPopover === "SUCCESS") {
          setDataState((prev) => {
            const newValue = [...(prev as Soc2Control[])];

            const findIndexOfEditingControl = newValue.findIndex(
              (d) => d.uid === control.uid
            );
            if (findIndexOfEditingControl > -1) {
              newValue[findIndexOfEditingControl].control =
                newValue[findIndexOfEditingControl].new_control ??
                newValue[findIndexOfEditingControl].control;
              newValue[findIndexOfEditingControl].new_control = undefined;
              newValue[findIndexOfEditingControl].changesInControl = 0;
              newValue[
                findIndexOfEditingControl
              ].changesInTestingProcedure = 1;
            }

            return newValue;
          });
          setShowAcceptAndRejectPopover(false);
          getCustomizeControlsActions().setNoOfGenerateWithAiPopoverOpens(
            (prev) => prev - 1
          );
          getCustomizeControlsActions().setChangesMade((prev) => prev + 1); // to trigger autosave
        }
      }
    },
    [handleClickForRefineControls, showAcceptAndRejectPopover, control.uid]
  );

  useEffect(() => {
    const el = document.getElementById("customize-control-table");
    if (showAcceptAndRejectPopover) {
      if (el) {
        if (
          getCustomizeControlsAiOpenStates() === 0 ||
          getCustomizeControlsAiOpenStates() === 1
        ) {
          containerRef.current?.scrollIntoView({});
          el.scrollBy(0, -50);
        }
        // Removed overflow-hidden logic to allow scrolling while popup is open
      }
    }
  }, [showAcceptAndRejectPopover]);

  useEffect(() => {
    const focusInHandler = (event: any) => {
      if (event?.target?.id?.includes(control.uid)) {
        setIsRowActive(true);
      }
    };
    const focusOutHandler = () => {
      setIsRowActive(false);
    };
    window.addEventListener("focusin", focusInHandler);
    window.addEventListener("focusout", focusOutHandler);
    return () => {
      window.removeEventListener("focusin", focusInHandler);
      window.removeEventListener("focusout", focusOutHandler);
    };
  }, [control]);

  return (
    <div
      className="h-full min-w-[280px] group"
      id={control.control_id}
      ref={containerRef}
    >
      <ZTextAreaWithOnChangeDelaySoc2
        id={`${control.uid}control`}
        aria-label={`${control.uid}control`}
        className="text-zinc-700 font-light bg-transparent"
        classNames={{
          base: "shadow-none border-none border-0",
          inputWrapper: "text-sm font-normal text-zinc-700 shadow-none border-none px-1 py-1",
          input: "p-0 border-none",
        }}
        forceActive={isLoading || showAcceptAndRejectPopover !== false}
        strikeContent={
          showAcceptAndRejectPopover !== false
            ? control.new_control !== undefined
            : undefined
        }
        minRows={3}
        maxRows={8}
        isDisabled={isDisabled || searchQuery.length > 0 || isCurrentSessionInReassessment}
        variant="bordered"
        labelPlacement="outside"
        value={control.control}
        placeholder="Add Control Description"
        onChange={(e) =>
          handleChangeDataState("control", control.uid, e.target.value)
        }
        autoFocus={false}
      />

      {control.new_control !== undefined && (
        <ZTextAreaWithOnChangeDelaySoc2
          id={`${control.uid}new_control`}
          aria-label={`${control.uid}new_control`}
          className="text-zinc-700 font-light bg-transparent"
          classNames={{
            base: "shadow-none border-none",
            inputWrapper: "text-sm font-normal text-zinc-700 shadow-none border-none px-1 py-1",
            input: "p-0",
          }}
          forceActive={true}
          strikeContent={false}
          minRows={3}
          maxRows={8}
          disabled={searchQuery.length > 0 || isCurrentSessionInReassessment}
          variant="bordered"
          labelPlacement="outside"
          value={control.new_control}
          placeholder="Add Control Description"
          onChange={(e) =>
            handleChangeDataState("new_control", control.uid, e.target.value)
          }
          autoFocus={false}
        />
      )}
      
      {/* Hide AI Generate buttons in reassessment mode */}
      {showAcceptAndRejectPopover && !isCurrentSessionInReassessment && (
        <div className="h-5">
          <AcceptAndRejectPopOver
            isOpen
            type={showAcceptAndRejectPopover}
            handleClickForAcceptRetryRejectButtons={
              handleClickForAcceptRetryRejectButtons
            }
            handleClose={() => setShowAcceptAndRejectPopover(false)}
          />
        </div>
      )}
      
      {!isDisabled && showAcceptAndRejectPopover === false && !isCurrentSessionInReassessment && (
        <div 
          className={cn(
            "group-hover:h-5 h-0 overflow-hidden ease-in-out duration-200",
            (isRowActive || isLoading || showAcceptAndRejectPopover) && "h-5"
          )}
        >
          <GenerateWithAI
            contextText={control.control}
            existingText={control.control}
            isLoadingAIResponse={isLoading}
            handleClickForRefineControls={handleClickForRefineControls}
            handleClickForGenerateControls={async () => {}}
            showGenerate={false}
            showRefine={true}
            type="CONTROL_DESCRIPTION"
            disableGenerate={false}
            disableGenerateTooltipMessage=""
            disableRefine={control?.control?.trim().length === 0}
            disableRefineTooltipMessage={[
              "You will be able to refine",
              "if new edits are made ",
              "to control description",
            ]}
          />
        </div>
      )}
    </div>
  );
};

interface CustomizeControlTestingProcedureRendererProps extends baseProps {}
export const CustomizeControlTestingProcedureRenderer = ({
  control,
  handleChangeDataState,
  searchQuery,
  isDisabled = false,
}: CustomizeControlTestingProcedureRendererProps) => {
  const { id = "" } = useParams<{ id: string }>();
  const isCurrentSessionInReassessment = useReassessmentDisabled();
  
  const [isRowActive, setIsRowActive] = useState(false);
  const { expandedTestingProcedures, actions: { setExpandedTestingProcedures } } = useCustomizeControlsStore();

  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showAcceptAndRejectPopover, setShowAcceptAndRejectPopover] = useState<
    false | "SUCCESS" | "ERROR"
  >(false);
  const [isEditing, setIsEditing] = useState(false);

  const isAccordion = control.edited_state === "ACCORDIAN";
  const isNew = control.edited_state === "NEW";
  const isCurrentlyExpanded = expandedTestingProcedures.has(control.uid);

  const testingProcedureText = control.new_toe || control.toe || "";
  const shouldShowSeeMore = testingProcedureText.length > 150;

  const toggleExpanded = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedTestingProcedures(prev => {
      const newSet = new Set(prev);
      if (newSet.has(control.uid)) {
        newSet.delete(control.uid);
      } else {
        newSet.add(control.uid);
      }
      return newSet;
    });
  };

  const refineControlPreviousCallThatFailedRef = useRef<"CREATE" | "REFINE">();
  const handleClickForRefineControls = useCallback(
    async (type: "CREATE" | "REFINE") => {
      try {
        setIsLoading(true);
        refineControlPreviousCallThatFailedRef.current = type;
        const agentData = getAgentData(id);
        let response_quality =
          agentData?.sessionData.type === AgentSessionType.SOC2_GAP
            ? agentData?.sessionData.soc2_type2_audit.response_quality
            : agentData?.sessionData.type === AgentSessionType.SOC2_GAP_TYPE1
              ? agentData?.sessionData.soc2_type1.response_quality
              : agentData?.sessionData.type === AgentSessionType.SOC2_GAP_TYPE2
                ? agentData?.sessionData.soc2_type2.response_quality
                : AiResponseType.LITE;

        if (
          response_quality === undefined ||
          response_quality === null ||
          response_quality === AiResponseType.NONE
        ) {
          response_quality = AiResponseType.LITE;
        }

        const resp = await refineControlsTestingProcedure({
          task: AgentSessionStepType.SOC2_TYPE2,
          controls: [
            {
              ...control,
              toe: type === "REFINE" ? control.toe : "",
              tod: type === "REFINE" ? control.tod : "",
            },
          ],
          response_quality,
        });

        if (resp.data.controls[0].toe) {
          const agentData = getAgentData(id);
          const newToe =
            agentData?.sessionData.type === AgentSessionType.SOC2_GAP_TYPE1
              ? resp.data.controls[0].tod
              : resp.data.controls[0].toe;
          if (control.toe) {
            handleChangeDataState("new_toe", control.uid, newToe);
            if (showAcceptAndRejectPopover === false) {
              getCustomizeControlsActions().setNoOfGenerateWithAiPopoverOpens(
                (prev) => prev + 1
              );
            }
            setShowAcceptAndRejectPopover("SUCCESS");
          } else {
            handleChangeDataState("toe", control.uid, newToe, -1);
          }
        }

        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        if (showAcceptAndRejectPopover === false) {
          getCustomizeControlsActions().setNoOfGenerateWithAiPopoverOpens(
            (prev) => prev + 1
          );
        }
        setShowAcceptAndRejectPopover("ERROR");
        throw new Error("Failed to refine control descroiption ");
      }
    },
    [control, handleChangeDataState, id, showAcceptAndRejectPopover]
  );

  const handleClickForAcceptRetryRejectButtons = useCallback(
    (action: "CONTINUE" | "DISCARD") => {
      const { setDataState } = getCustomizeControlsActions();

      if (action === "DISCARD") {
        setShowAcceptAndRejectPopover(false);
        getCustomizeControlsActions().setNoOfGenerateWithAiPopoverOpens(
          (prev) => prev - 1
        );
        setDataState((prev) => {
          const newValue = [...(prev as Soc2Control[])];

          const findIndexOfEditingControl = newValue.findIndex(
            (d) => d.uid === control.uid
          );
          if (findIndexOfEditingControl > -1) {

            newValue[findIndexOfEditingControl].new_toe = undefined;

          }

          return newValue;
        });
      }
      if (action === "CONTINUE") {
        if (showAcceptAndRejectPopover === "ERROR") {
          handleClickForRefineControls(
            refineControlPreviousCallThatFailedRef.current ?? "REFINE"
          );
        }
        if (showAcceptAndRejectPopover === "SUCCESS") {
          setDataState((prev) => {
            const newValue = [...(prev as Soc2Control[])];

            const findIndexOfEditingControl = newValue.findIndex(
              (d) => d.uid === control.uid
            );
            if (findIndexOfEditingControl > -1) {
              newValue[findIndexOfEditingControl].toe =
                newValue[findIndexOfEditingControl].new_toe ??
                newValue[findIndexOfEditingControl].toe;
              newValue[findIndexOfEditingControl].new_toe = undefined;
              newValue[findIndexOfEditingControl].changesInTestingProcedure =
                refineControlPreviousCallThatFailedRef.current === "CREATE"
                  ? -1
                  : 0;

            }

            return newValue;
          });
          setShowAcceptAndRejectPopover(false);
          getCustomizeControlsActions().setNoOfGenerateWithAiPopoverOpens(
            (prev) => prev - 1
          );
          getCustomizeControlsActions().setChangesMade((prev) => prev + 1); // to trigger autosave
        }
      }
    },
    [handleClickForRefineControls, showAcceptAndRejectPopover, control.uid]
  );

  useEffect(() => {
    const el = document.getElementById("customize-control-table");
    if (showAcceptAndRejectPopover) {
      if (el) {
        if (
          getCustomizeControlsAiOpenStates() === 0 ||
          getCustomizeControlsAiOpenStates() === 1
        ) {
          containerRef.current?.scrollIntoView({});
          el.scrollBy(0, -100);
        }
        // Removed overflow-hidden logic to allow scrolling while popup is open
      }
    }
  }, [showAcceptAndRejectPopover]);

  useEffect(() => {
    const focusInHandler = (event: any) => {
      if (event?.target?.id?.includes(control.uid)) {
        setIsRowActive(true);
      }
    };
    const focusOutHandler = () => {
      setIsRowActive(false);
    };
    window.addEventListener("focusin", focusInHandler);
    window.addEventListener("focusout", focusOutHandler);
    return () => {
      window.removeEventListener("focusin", focusInHandler);
      window.removeEventListener("focusout", focusOutHandler);
    };
  }, [control]);

  // Add useEffect to handle clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsEditing(false);
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isEditing]);

  if (isAccordion) {
    return (
      <div className="flex w-full flex-col gap-1">
        <div className="w-full">
          {isEditing && !isCurrentSessionInReassessment ? (
            <ZTextAreaWithOnChangeDelay
              forceActive={true}
              key={control.uid + "toe_edit"}
              id={control.uid + "toe_edit"}
              value={control.toe}
              onChange={(e) => {
                handleChangeDataState(
                  "toe",
                  control.uid,
                  e.target.value,
                  (control.changesInTestingProcedure ?? 0) + 1
                );
              }}
              onBlur={() => setIsEditing(false)}
              className="w-full min-h-[120px]"
              placeholder="Add Testing Procedure"
              autoFocus
            />
          ) : (
            <div
              className={cn(
                "text-zinc-700 leading-5 text-sm",
                !isCurrentSessionInReassessment && "cursor-text"
              )}
              onClick={(e) => {
                if (isCurrentSessionInReassessment) return;
                const target = e.target as HTMLElement;
                if (target.tagName !== 'BUTTON') {
                  setIsEditing(true);
                }
              }}
            >
              {isCurrentlyExpanded || !shouldShowSeeMore ? (
                control.toe
              ) : (
                <>
                  {control.toe.substring(0, 150)}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleExpanded(e);
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium ml-1"
                  >
                    ...more
                  </button>
                </>
              )}
              {isCurrentlyExpanded && shouldShowSeeMore && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleExpanded(e);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium ml-1"
                >
                    ...less
                </button>
              )}
            </div>
          )}
        </div>
        <div className="w-full">
          <ZTextAreaWithOnChangeDelay
            forceActive={true}
            key={control.uid + "toe"}
            id={control.uid + "toe"}
            value={control.new_toe || ""}
            onChange={(e) => {
              handleChangeDataState(
                "new_toe",
                control.uid,
                e.target.value,
                (control.changesInTestingProcedure ?? 0) + 1
              );
            }}
            className="w-full min-h-[120px] border-0"
            placeholder="Add Testing Procedure"
            isDisabled={isDisabled || isCurrentSessionInReassessment}
          />
        </div>
      </div>
    );
  }

  if (isNew) {
    return (
      <ZTextAreaWithOnChangeDelay
        key={control.uid + "toe"}
        id={control.uid + "toe"}
        value={control.toe}
        onChange={(e) => {
          handleChangeDataState(
            "toe",
            control.uid,
            e.target.value,
            (control.changesInTestingProcedure ?? 0) + 1
          );
        }}
        className={cn(
          "text-zinc-700 font-light bg-transparent  focus-within:text-[#000000] mb-[5px]",
          control.new_control !== undefined && "mb-0"
        )}
        classNames={{
          base:
            "shadow-none  border-none hover:border-none rounded-none truncate",
          inputWrapper: ` text-sm font-normal text-zinc-700 p-px ${true ? "border-none " : ""
            }  ${false ? "text-[#000000]" : "text-zinc-700"
            } border-1 border-[#ECECEC]`,
          input: "p-[4px]",
        }}
        placeholder="Add Testing Procedure"
        variant="bordered"
        labelPlacement="outside"
        isDisabled={isCurrentSessionInReassessment}
      />
    );
  }

  // Default view with existing AI functionality and see more
  const hasChanges = control.changesInTestingProcedure !== undefined && control.changesInTestingProcedure > 0;
  // Show "see more" if text is long enough AND we're not editing AND there's no AI-generated content
  const showReadOnlyWithSeeMore = shouldShowSeeMore && !isEditing && !control.new_toe;

  return (
    <div className="min-w-[280px] group" ref={containerRef}>
      {showReadOnlyWithSeeMore ? (
        <div
          className="mb-[5px] cursor-text"
          onClick={(e) => {
            if (isCurrentSessionInReassessment) return;
            const target = e.target as HTMLElement;
            if (!isDisabled && target.tagName !== 'BUTTON') {
              setIsEditing(true);
            }
          }}
        >
          <div className={clsx(" leading-5 text-sm whitespace-pre-wrap", (isDisabled || isCurrentSessionInReassessment) ? "text-zinc-400" : "text-zinc-700")}>
            {isCurrentlyExpanded ? (
              <>
                {testingProcedureText}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleExpanded(e);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium ml-1"
                >
                  ...less
                </button>
              </>
            ) : (
              <>
                {testingProcedureText.substring(0, 150)}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    toggleExpanded(e);
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium ml-1"
                >
                    ...more
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
          <ZTextAreaWithOnChangeDelaySoc2
          id={`${control.uid}toe`}
          aria-label={`${control.uid}toe`}
            className="text-zinc-700 font-light bg-transparent"
          classNames={{
            base: "shadow-none border-none border-0",
            inputWrapper: "text-sm font-normal text-zinc-700 shadow-none border-none px-1 py-1",
            input: "p-0",
          }}
          forceActive={isLoading || showAcceptAndRejectPopover !== false || isEditing}
          strikeContent={
            showAcceptAndRejectPopover !== false
              ? control.new_toe !== undefined
              : undefined
          }
          minRows={3}
          maxRows={8}
          variant="bordered"
          labelPlacement="outside"
          value={control.toe}
            placeholder="Add Testing Procedure"
          onChange={(e) =>
            handleChangeDataState("toe", control.uid, e.target.value)
          }
          onBlur={() => setIsEditing(false)}
          autoFocus={isEditing && !isCurrentSessionInReassessment}
          isDisabled={isDisabled || searchQuery.length > 0 || isCurrentSessionInReassessment}
        />
      )}

      {control.new_toe !== undefined && (
        <ZTextAreaWithOnChangeDelaySoc2
          id={`${control.uid}new_toe`}
          aria-label={`${control.uid}new_toe`}
          className="text-zinc-700 font-light bg-transparent border-0"
          classNames={{
            base: "shadow-none border-none",
            inputWrapper: "text-sm font-normal text-zinc-700 shadow-none border-none px-1 py-1",
            input: "p-0",
          }}
          forceActive={true}
          strikeContent={false}
          minRows={3}
          maxRows={8}
          disabled={searchQuery.length > 0 || isCurrentSessionInReassessment}
          variant="bordered"
          labelPlacement="outside"
          value={control.new_toe}
          placeholder="Add Testing Procedure"
          onChange={(e) =>
            handleChangeDataState("new_toe", control.uid, e.target.value)
          }
          autoFocus={false}
        />
      )}

      {/* Hide AI features in reassessment mode */}
      {showAcceptAndRejectPopover && !isCurrentSessionInReassessment && (
        <div className="h-5">
          <AcceptAndRejectPopOver
            isOpen
            type={showAcceptAndRejectPopover}
            handleClickForAcceptRetryRejectButtons={
              handleClickForAcceptRetryRejectButtons
            }
            handleClose={() => setShowAcceptAndRejectPopover(false)}
          />
        </div>
      )}

      {!isDisabled && showAcceptAndRejectPopover === false && !isCurrentSessionInReassessment && (
        <div 
          className={cn(
            "group-hover:h-5 h-0 overflow-hidden ease-in-out duration-200",
            (isRowActive || isLoading || showAcceptAndRejectPopover) && "h-5"
          )}
        >
          <GenerateWithAI
            contextText={control.toe}
            existingText={control.toe}
            isLoadingAIResponse={isLoading}
            handleClickForRefineControls={() => handleClickForRefineControls("REFINE")}
            handleClickForGenerateControls={() => handleClickForRefineControls("CREATE")}
            showGenerate={control.toe?.trim().length === 0}
            showRefine={control.toe?.trim().length > 0}
            type="TESTING_PROCEDURE"
            disableGenerate={control.control.trim().length === 0}
            disableGenerateTooltipMessage="You will be able to generate if control description is added"
            disableRefine={
              control.toe?.trim().length === 0 ||
              control.control.trim().length === 0
            }
            disableRefineTooltipMessage={[
              "You will be able to refine",
              "if new edits are made ",
              "to testing procedure",
            ]}
          />
        </div>
      )}
    </div>
  );
};

export const CustomizeControlSoc2DocumentUploadRenderer = ({
  control,
  allControls,
  currentIndex,
}: {
  control: Soc2Control | CustomGapAssessmentControl;
  allControls?: Soc2Control[];
  currentIndex?: number;
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isReassessModalOpen, setIsReassessModalOpen] = useState(false);
  const [activeControlIndex, setActiveControlIndex] = useState<number | undefined>(currentIndex);

  const selectedTab = useCustomizeControlsStore().selectedTab;

  // Get reassessment mode state
  const { isReassessmentMode, agentId: reassessmentAgentId } = useReassessmentModeStore();
  const { id: currentAgentId } = useParams<{ id: string }>();
  const isCurrentSessionInReassessment = isReassessmentMode && currentAgentId === reassessmentAgentId;

  // Get all controls from state if not provided
  const allSoc2Controls = useMemo(() => {
    if (allControls) return allControls;
    const controls = getCustomizeControls() as Soc2Control[];
    return controls;
  }, [allControls]);

  // Calculate the active control based on the index
  const activeControl: any = useMemo(() => {
    if (activeControlIndex !== undefined && allSoc2Controls && allSoc2Controls.length > 0 &&
      activeControlIndex >= 0 && activeControlIndex < allSoc2Controls.length) {
      return allSoc2Controls[activeControlIndex];
    }
    return control;
  }, [activeControlIndex, allSoc2Controls, control]);

  const uploadModalName = useMemo(() => {
    if((control?.agentType) === AgentSessionType.GAP_CUSTOM_FRAMEWORK){
      return `${activeControl.controlIdPrefix} ${activeControl.control_id || ""} : ${activeControl?.control}`
    }
    if(selectedTab && activeControl){
      const thisSelectedTab = selectedTab as keyof typeof TSCABBRIVATIONS
      const selectedTabAbbriviation = TSCABBRIVATIONS[thisSelectedTab]
      return `${activeControl.tsc_id?.replace(selectedTabAbbriviation, `${selectedTabAbbriviation} `)} (${UploadModalServiceCriteriaLabel[thisSelectedTab]} / ${activeControl.category})`
    }
    return ""
  }, [selectedTab, activeControl.tsc_id, activeControl.control_id])

  // Find current index if not provided
  const calculatedCurrentIndex = useMemo(() => {
    if (currentIndex !== undefined) return currentIndex;
    return allSoc2Controls.findIndex(c => c.uid === control.uid);
  }, [currentIndex, control.uid, allSoc2Controls]);

  // Update activeControlIndex when calculatedCurrentIndex changes
  useEffect(() => {
    setActiveControlIndex(calculatedCurrentIndex);
  }, [calculatedCurrentIndex]);

  const handlePrevControl = useCallback(() => {
    if (!allSoc2Controls || activeControlIndex === undefined || activeControlIndex <= 0) return;
    setActiveControlIndex(activeControlIndex - 1);
  }, [activeControlIndex, allSoc2Controls]);

  const handleNextControl = useCallback(() => {
    if (!allSoc2Controls || activeControlIndex === undefined) return;
    if(activeControlIndex >= allSoc2Controls.length - 1){
      setActiveControlIndex(0)
    }else{
      setActiveControlIndex(activeControlIndex + 1);
    }
  }, [activeControlIndex, allSoc2Controls]);

  const handleUpdateSourceUrls = useCallback((newUrls: string[], newFileNames?: string[]) => {
    if (!activeControl) return;

    // In non-reassessment mode, update source_urls directly
    if (!isCurrentSessionInReassessment) {
      // Update the control's source_urls using the store's setDataState action
      getCustomizeControlsActions().setDataState((prevControls) => {
        return prevControls.map(c => {
          if (c.uid === activeControl.uid) {
            return { ...c, source_urls: newUrls, file_names: newFileNames || [] };
          }
          return c;
        });
      });

      // Trigger autosave by incrementing the changesMade counter
      getCustomizeControlsActions().setChangesMade((prev) => prev + 1);
    } else {
      // In reassessment mode, keep permanent files and temporary files separate
      getCustomizeControlsActions().setDataState((prevControls) => {
        return prevControls.map(c => {
          if (c.uid === activeControl.uid) {
            return {
              ...c,
              // Keep original source_urls unchanged
              // Update the temporary files
              new_uploaded_files: newUrls,
              new_uploaded_file_names: newFileNames || []
            };
          }
          return c;
        });
      });
    }
  }, [activeControl, isCurrentSessionInReassessment]);

  const handleOpenModal = useCallback(() => {
    setActiveControlIndex(calculatedCurrentIndex);

    // Choose which modal to open based on reassessment mode
    if (isCurrentSessionInReassessment) {
      setIsReassessModalOpen(true);
    } else {
      setIsModalOpen(true);
    }
  }, [calculatedCurrentIndex, isCurrentSessionInReassessment]);

  // Get file count directly from the store
  const currentControl = getCustomizeControls().find(c => c.uid === control.uid) || control;
  const typedControl = currentControl as Soc2Control;

  const fileNames = typedControl?.file_names || []

  const fileCount = (typedControl.source_urls?.length || 0) + (typedControl.new_uploaded_files?.length || 0);
  const hasUploadedFiles = fileCount > 0;

  const fileListUI = useMemo(() => {

    return <div className="max-h-[150px] overflow-auto pr-2">
      {fileNames.map((uploadedFileName: string) => (
        <div>{uploadedFileName}
        </div>
      ))}
    </div>

  }, [fileNames])

   return (
    <div className="flex justify-center">
      {hasUploadedFiles ? (
        <div className="flex items-center">
          <ZTooltip content={fileListUI} showArrow placement="left">
            <div className="flex items-center justify-center w-4 h-4 bg-gray-300 rounded-full mr-2">
              <span className="text-[10px] font-medium">{fileCount}</span>
            </div>
          </ZTooltip>
          <ZTooltip content={"Upload More"} showArrow placement="bottom-end">
            <div>
            <ZButton
              size="sm"
              variant="bordered"
              color="primary"
              className="min-w-0 w-12 h-6 p-0 flex justify-center items-center"
              onClick={handleOpenModal}
              >
              <Upload size={14} />
            </ZButton>
              </div>
          </ZTooltip>
        </div>
      ) : (
        <ZButton
          size="sm"
          variant="solid"
          color="primary"
          className="px-3 py-1 h-auto"
          onClick={handleOpenModal}
        >
          Upload
        </ZButton>
      )}

      {/* Only show UploadDocumentsModal when NOT in reassessment mode for this session */}
      {!isCurrentSessionInReassessment && (
        <UploadDocumentsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          controlId={activeControl.control_id}
          controlName={uploadModalName}
          controlDescription={activeControl.control || ""}
          onPrevControl={allSoc2Controls && activeControlIndex !== undefined && activeControlIndex > 0 ? handlePrevControl : undefined}
          onNextControl={allSoc2Controls && activeControlIndex !== undefined ? handleNextControl : undefined}
          currentIndex={activeControlIndex}
          totalControls={allSoc2Controls?.length}
          sourceUrls={activeControl.source_urls || []}
          fileNames={activeControl.file_names || []}
          onUpdateSourceUrls={handleUpdateSourceUrls}
          showSaveSuccess
        />
      )}

      {/* Only show ReassessDocumentsModal when in reassessment mode for this specific session */}
      {isCurrentSessionInReassessment && (
        <ReassessDocumentsModal
          isOpen={isReassessModalOpen}
          onClose={() => setIsReassessModalOpen(false)}
          agentId={currentAgentId || ""}
          controlName={uploadModalName}
          controlDescription={activeControl.control || ""}
          control={activeControl}
          controlIndex={activeControlIndex}
          onPrevControl={allSoc2Controls && activeControlIndex !== undefined && activeControlIndex > 0 ? handlePrevControl : undefined}
          onNextControl={allSoc2Controls && activeControlIndex !== undefined ? handleNextControl : undefined}
          totalControls={allSoc2Controls?.length}
          showSaveSuccess
        />
      )}
    </div>
  );
};